import { type FormEvent, useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useToast } from "../components/Toast";

/** Admin-only server upstream pull API configuration. */
export function UpstreamSettingsPanel() {
  const { push } = useToast();
  const [apiLocation, setApiLocation] = useState("");
  const [apiAccessToken, setApiAccessToken] = useState("");
  const [markUrl, setMarkUrl] = useState("");
  const [fallbackProxy, setFallbackProxy] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const data = await api<{
      apiLocation: string;
      apiAccessToken: string;
      hasToken: boolean;
      markUrl: string;
      fallbackProxy?: string;
    }>("/api/admin/settings/upstream");
    setApiLocation(data.apiLocation || "");
    setApiAccessToken(data.hasToken ? "••••••••" : "");
    setMarkUrl(data.markUrl || "");
    setFallbackProxy(data.fallbackProxy || "");
    setLoaded(true);
  }, []);

  useEffect(() => {
    void load().catch((err) => {
      push(err instanceof Error ? err.message : "Failed to load upstream settings", "error");
      setLoaded(true);
    });
  }, [load, push]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const tokenForSave =
        apiAccessToken === "••••••••" ? undefined : apiAccessToken;
      await api("/api/admin/settings/upstream", {
        method: "PUT",
        body: JSON.stringify({
          apiLocation,
          markUrl,
          fallbackProxy,
          ...(tokenForSave !== undefined ? { apiAccessToken: tokenForSave } : {}),
        }),
      });
      push("Server upstream settings saved", "ok");
      await load();
    } catch (err) {
      push(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="settings-form surface upstream-panel" onSubmit={(e) => void onSave(e)}>
      <fieldset>
        <legend>Upstream pull API (server)</legend>
        <p className="muted tiny" style={{ margin: 0 }}>
          Admin-only. Configures the server poller — not stored in the desktop app.
        </p>
        {!loaded ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <label>
              <span>API location</span>
              <input
                value={apiLocation}
                onChange={(e) => setApiLocation(e.target.value)}
                placeholder="https://your-api.example/v1/pull"
                disabled={busy}
              />
            </label>
            <label>
              <span>Access token</span>
              <input
                type="text"
                autoComplete="off"
                value={apiAccessToken}
                onChange={(e) => setApiAccessToken(e.target.value)}
                placeholder="Bearer token"
                disabled={busy}
              />
            </label>
            <label>
              <span>Mark URL (optional)</span>
              <input
                value={markUrl}
                onChange={(e) => setMarkUrl(e.target.value)}
                placeholder="Defaults to {API location}/mark"
                disabled={busy}
              />
            </label>
            <label>
              <span>Fallback SOCKS5 (optional)</span>
              <input
                value={fallbackProxy}
                onChange={(e) => setFallbackProxy(e.target.value)}
                placeholder="socks5h://user:pass_session-{session}@host:port"
                disabled={busy}
              />
            </label>
            <p className="muted tiny" style={{ margin: 0 }}>
              Used when a pulled account has no proxy. Placeholders:{" "}
              <code>{"{session}"}</code>, <code>{"{unique_id}"}</code>, <code>{"{id}"}</code>,{" "}
              <code>{"{visitorId}"}</code>.
            </p>
            <div className="settings-actions">
              <button type="submit" disabled={busy}>
                {busy ? "Saving…" : "Save upstream settings"}
              </button>
            </div>
          </>
        )}
      </fieldset>
    </form>
  );
}
